import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  X, Radio, Eye, Share2, Volume2, VolumeX, Heart, 
  MessageCircle, ShoppingBag, ChevronRight, Send, 
  Package, User, Crown, Gavel, Check, Sparkles,
  Search, RefreshCw, ChevronUp, ChevronDown, Timer, Trophy, Wifi, WifiOff
} from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import { useAgora } from "@/hooks/useAgora";
import YouTubePlayer from "./YouTubePlayer";

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Mock chat messages for demo (fallback when no WebSocket messages)
const MOCK_CHAT = [
  { id: 1, user: 'fashionista_22', message: 'OMG love this! 😍', avatar: 'F' },
  { id: 2, user: 'collector_mike', message: 'Is this authentic?', avatar: 'C' },
  { id: 3, user: 'deal_hunter', message: 'Great price!', avatar: 'D' },
  { id: 4, user: 'shopper_jane', message: 'What size is this?', avatar: 'S' },
  { id: 5, user: 'style_guru', message: 'Beautiful piece 🔥', avatar: 'S' },
];

// Mock products for demo
const MOCK_PRODUCTS = [
  { id: 1, title: "Designer Handbag - Limited Edition", price: 99, originalPrice: 199, size: "One Size", brand: "Coach", image: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400", sold: false, likes: 8 },
  { id: 2, title: "Vintage Sneakers Collection", price: 75, size: "10", brand: "Nike", image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400", sold: true, likes: 12 },
  { id: 3, title: "Antique Watch - 1960s Era", price: 250, size: "N/A", brand: "Omega", image: "https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=400", sold: false, likes: 5 },
  { id: 4, title: "Herve Leger Bandage Dress", price: 369, size: "XS", brand: "Herve Leger", image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400", sold: false, likes: 17, nwt: true },
  { id: 5, title: "Off-White Second Skin Top", price: 399, size: "XS", brand: "Off-White", image: "https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=400", sold: false, likes: 2 },
];

const LiveWatch = ({ user }) => {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(false);
  const [liked, setLiked] = useState(false);
  const [chatMessages, setChatMessages] = useState(MOCK_CHAT);
  const [chatInput, setChatInput] = useState('');
  const [currentProduct, setCurrentProduct] = useState(MOCK_PRODUCTS[0]);
  const [products, setProducts] = useState(MOCK_PRODUCTS);
  const [viewerCount, setViewerCount] = useState(85);
  const [auctionStatus, setAuctionStatus] = useState('idle'); // idle, active, countdown, sold
  const [winner, setWinner] = useState(null);
  const [joinNotification, setJoinNotification] = useState(null);
  const [searchListings, setSearchListings] = useState('');
  const [showPromo, setShowPromo] = useState(true);
  const [agoraInitialized, setAgoraInitialized] = useState(false);
  const agoraInitRef = useRef(false); // Ref to prevent double initialization in StrictMode
  
  // Auction state
  const [auction, setAuction] = useState(null);
  const [auctionCountdown, setAuctionCountdown] = useState(null);
  const [currentBid, setCurrentBid] = useState(null);
  const [highBidder, setHighBidder] = useState(null);
  const [bidInput, setBidInput] = useState('');
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  
  // WebSocket
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const videoContainerRef = useRef(null);
  const desktopVideoContainerRef = useRef(null);
  
  // Agora hook for receiving video stream
  const {
    isConnected: agoraConnected,
    error: agoraError,
    remoteUsers,
    connectionState,
    joinAsViewer,
    leave: agoraLeave
  } = useAgora(streamId);

  // WebSocket connection with reconnection logic
  const wsReconnectAttempts = useRef(0);
  const wsReconnectTimer = useRef(null);
  const wsMaxReconnects = 5;
  const wsIsUnmounting = useRef(false);
  
  const connectWebSocket = useCallback(() => {
    // Don't connect if unmounting or max attempts reached
    if (wsIsUnmounting.current || wsReconnectAttempts.current >= wsMaxReconnects) {
      return;
    }
    
    // Clean up any existing connection
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/live/ws/${streamId}?user_id=${user?.user_id || 'guest'}&user_name=${encodeURIComponent(user?.name || 'Guest')}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected for stream:', streamId);
        wsReconnectAttempts.current = 0; // Reset on successful connection
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          // Silently ignore parse errors
        }
      };
      
      wsRef.current.onclose = (event) => {
        // Only reconnect if not unmounting and visible
        if (!wsIsUnmounting.current && document.visibilityState === 'visible') {
          wsReconnectAttempts.current++;
          if (wsReconnectAttempts.current < wsMaxReconnects) {
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s
            const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts.current - 1), 16000);
            wsReconnectTimer.current = setTimeout(connectWebSocket, delay);
          }
        }
      };
      
      wsRef.current.onerror = () => {
        // Error is handled by onclose, don't log to avoid console spam
      };
    } catch {
      // Connection failed, will be handled by reconnection logic
    }
  }, [streamId, user]);

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'chat':
        setChatMessages(prev => [...prev.slice(-20), {
          id: Date.now(),
          user: data.sender_name,
          message: data.content,
          avatar: data.sender_name?.[0]?.toUpperCase() || 'U',
          isMe: data.sender_id === user?.user_id
        }]);
        break;
        
      case 'viewer_joined':
        setViewerCount(data.viewer_count);
        break;
        
      case 'viewer_left':
        setViewerCount(data.viewer_count);
        break;
        
      case 'auction_started':
        setAuction(data);
        setAuctionStatus('active');
        setCurrentBid(data.starting_price);
        setHighBidder(null);
        setAuctionCountdown(data.duration);
        toast.success(`Auction started for ${data.listing_title}!`, {
          icon: <Gavel className="h-4 w-4" />
        });
        break;
        
      case 'auction_countdown':
        setAuctionCountdown(data.remaining_seconds);
        if (data.current_bid) setCurrentBid(data.current_bid);
        if (data.high_bidder) setHighBidder(data.high_bidder);
        break;
        
      case 'auction_warning':
        if (data.remaining_seconds <= 3) {
          toast.info(data.message, { duration: 1000 });
        }
        break;
        
      case 'new_bid':
        setCurrentBid(data.amount);
        setHighBidder(data.bidder_name);
        setChatMessages(prev => [...prev.slice(-20), {
          id: Date.now(),
          user: 'BidZuu',
          message: `💰 ${data.bidder_name} bid $${data.amount}!`,
          avatar: '💰',
          isBid: true
        }]);
        break;
        
      case 'auction_won':
        setAuctionStatus('sold');
        setWinner(data.winner_name);
        toast.success(`🎉 ${data.winner_name} won for $${data.winning_bid}!`, {
          duration: 5000
        });
        // Reset after 5 seconds
        setTimeout(() => {
          setAuction(null);
          setAuctionStatus('idle');
          setWinner(null);
          setCurrentBid(null);
          setHighBidder(null);
        }, 5000);
        break;
        
      case 'auction_ended_no_winner':
        setAuctionStatus('idle');
        setAuction(null);
        toast.info('Auction ended with no bids');
        break;
        
      case 'product_featured':
        setCurrentProduct({
          id: data.listing_id,
          title: data.listing_title,
          price: data.listing_price,
          image: data.listing_image,
          brand: '',
          size: ''
        });
        toast.info(`Now featuring: ${data.listing_title}`);
        break;
        
      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  };

  // Send chat message via WebSocket
  const sendChatMessage = (message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'chat',
        content: message,
        sender_name: user?.name || 'Guest'
      }));
    }
  };

  // Place bid via API
  const placeBid = async (amount) => {
    if (!user) {
      toast.error('Please sign in to place a bid');
      return;
    }
    
    if (!auction) {
      toast.error('No active auction');
      return;
    }
    
    setIsPlacingBid(true);
    try {
      const response = await api.post(`/live/streams/${streamId}/auction/bid`, {
        amount: parseFloat(amount)
      });
      
      if (response.data.success) {
        toast.success(`Bid placed: $${amount}`);
        setBidInput('');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to place bid';
      toast.error(errorMsg);
    } finally {
      setIsPlacingBid(false);
    }
  };

  // Initialize Agora viewer
  const initializeAgoraViewer = useCallback(async () => {
    // Use ref for synchronous check to prevent race conditions in StrictMode
    if (agoraInitRef.current || !streamId) return;
    agoraInitRef.current = true; // Set immediately before any async operations
    
    try {
      const success = await joinAsViewer();
      if (success) {
        setAgoraInitialized(true);
        console.log('[LiveWatch] Joined Agora as viewer');
      } else {
        // Reset ref if join failed
        agoraInitRef.current = false;
      }
    } catch (err) {
      console.error('[LiveWatch] Agora init error:', err);
      agoraInitRef.current = false; // Reset ref on error
    }
  }, [streamId, joinAsViewer]);

  // Play remote video when available
  useEffect(() => {
    if (remoteUsers.length > 0) {
      const mainUser = remoteUsers[0];
      if (mainUser.videoTrack) {
        // Play on mobile container
        if (videoContainerRef.current) {
          mainUser.videoTrack.play(videoContainerRef.current);
        }
        // Play on desktop container
        if (desktopVideoContainerRef.current) {
          mainUser.videoTrack.play(desktopVideoContainerRef.current);
        }
      }
    }
  }, [remoteUsers]);

  // Show Agora errors
  useEffect(() => {
    if (agoraError) {
      console.error('[LiveWatch] Agora error:', agoraError);
    }
  }, [agoraError]);

  useEffect(() => {
    const fetchStream = async () => {
      try {
        const res = await api.get(`/live/streams/${streamId}`);
        setStream(res.data);
        setViewerCount(res.data.viewer_count || Math.floor(Math.random() * 200) + 50);
        
        // Only initialize Agora for non-YouTube streams
        if (res.data.stream_type !== 'youtube') {
          initializeAgoraViewer();
        }
        
        // Check for active auction
        const auctionRes = await api.get(`/live/streams/${streamId}/auction`);
        if (auctionRes.data.active) {
          setAuction(auctionRes.data.auction);
          setAuctionStatus('active');
          setCurrentBid(auctionRes.data.auction.current_bid);
          setHighBidder(auctionRes.data.auction.high_bidder_name);
        }
      } catch (err) {
        console.error('Error fetching stream:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStream();
    
    // Delay WebSocket connection slightly to avoid StrictMode race condition
    const wsConnectTimeout = setTimeout(() => {
      if (!wsIsUnmounting.current) {
        connectWebSocket();
      }
    }, 100);
    
    // Note: Agora initialization moved to after stream fetch to check stream_type

    // Fallback: Simulate viewer joins if WebSocket isn't working
    const joinInterval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        const names = ['sarah_style', 'mike_deals', 'fashion_lover', 'bargain_queen', 'collector_pro', 'julwy', 'anndarling'];
        const randomName = names[Math.floor(Math.random() * names.length)];
        setJoinNotification(randomName);
        setViewerCount(v => v + 1);
        setTimeout(() => setJoinNotification(null), 3000);
      }
    }, 8000);

    // Fallback: Simulate chat messages if WebSocket isn't working
    const chatInterval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        const messages = [
          'Love this! 😍', 'How much?', 'Is shipping free?', 'Great deal!', 
          'What was it', 'Size 8?', 'Whaaa', 'I missed it', 'Keep it!!! Looks great on you 💕',
          'You definitely provide the vision girl! 😊', 'Congratulations', 'It\'s so good guys you won\'t be disappointed'
        ];
        const names = ['buyer_22', 'style_fan', 'deal_seeker', 'fashion_pro', 'smalberg2013', 'ldunlap', 'wendys_dog_hous', 'courtneydnguyen'];
        const newMsg = {
          id: Date.now(),
          user: names[Math.floor(Math.random() * names.length)],
          message: messages[Math.floor(Math.random() * messages.length)],
          avatar: names[0][0].toUpperCase()
        };
        setChatMessages(prev => [...prev.slice(-12), newMsg]);
      }
    }, 4000);

    return () => {
      clearInterval(joinInterval);
      clearInterval(chatInterval);
      clearTimeout(wsConnectTimeout);
      agoraInitRef.current = false; // Reset on unmount for StrictMode
      wsIsUnmounting.current = true; // Prevent WebSocket reconnection
      if (wsReconnectTimer.current) {
        clearTimeout(wsReconnectTimer.current);
      }
      agoraLeave();
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {}
        wsRef.current = null;
      }
    };
  }, [streamId, connectWebSocket, initializeAgoraViewer, agoraLeave]);

  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    
    // Send via WebSocket if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      sendChatMessage(chatInput);
    } else {
      // Fallback to local state
      const newMsg = {
        id: Date.now(),
        user: user?.name?.split(' ')[0] || 'You',
        message: chatInput,
        avatar: user?.name?.[0] || 'Y',
        isMe: true
      };
      setChatMessages(prev => [...prev.slice(-12), newMsg]);
    }
    setChatInput('');
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/live/${streamId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    } catch (err) {}
  };

  const handleBid = () => {
    if (auction && auctionStatus === 'active') {
      // Real auction - place bid
      const minBid = (currentBid || 0) + 1;
      placeBid(bidInput || minBid);
    } else {
      // Demo mode - simulate bid
      setTimeout(() => {
        setWinner(user?.name?.split(' ')[0] || 'anndarling');
        setAuctionStatus('sold');
        setCurrentProduct(prev => ({ ...prev, sold: true }));
        setTimeout(() => {
          setWinner(null);
          setAuctionStatus('idle');
          const currentIdx = products.findIndex(p => p.id === currentProduct.id);
          if (currentIdx < products.length - 1) {
            setCurrentProduct(products[currentIdx + 1]);
          }
        }, 5000);
      }, 1500);
      toast.success('Bid placed!');
    }
  };

  const filteredProducts = products.filter(p => 
    p.title.toLowerCase().includes(searchListings.toLowerCase()) ||
    p.brand.toLowerCase().includes(searchListings.toLowerCase())
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // ============ MOBILE VIEW ============
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col" data-testid="mobile-live-watch">
        {/* Video Background - Agora Container or YouTube Player */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 to-black">
          {/* YouTube Player - when stream has youtube_video_id */}
          {stream?.youtube_video_id ? (
            <YouTubePlayer
              videoId={stream.youtube_video_id}
              title={stream?.title}
              thumbnail={stream?.thumbnail}
              autoplay={true}
              muted={muted}
              className="w-full h-full"
            />
          ) : (
            <>
              {/* Agora Video Container */}
              <div 
                ref={videoContainerRef} 
                className="w-full h-full"
                style={{ background: '#000' }}
              />
              
              {/* Fallback when no video stream */}
              {remoteUsers.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                  {stream?.thumbnail ? (
                    <img src={stream.thumbnail} alt="" className="w-full h-full object-cover opacity-90" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                      {agoraConnected ? (
                        <div className="text-center">
                          <Radio className="h-20 w-20 text-slate-600 animate-pulse mx-auto mb-4" />
                          <p className="text-slate-400">Waiting for host to start streaming...</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
                          <p className="text-slate-400">Connecting to stream...</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none" />
        </div>

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between p-3 safe-area-top">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/live')} className="w-9 h-9 flex items-center justify-center">
              <X className="h-6 w-6 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Avatar className="w-10 h-10 border-2 border-white">
                  <AvatarImage src={stream?.host_picture} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                    {stream?.host_name?.[0] || 'H'}
                  </AvatarFallback>
                </Avatar>
                <button className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border border-white">
                  <span className="text-white text-xs font-bold">+</span>
                </button>
              </div>
              <span className="text-white font-semibold text-sm">@{stream?.host_name || 'host'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1 ${remoteUsers.length > 0 ? 'bg-red-500' : agoraConnected ? 'bg-yellow-500' : 'bg-slate-500'} px-3 py-1 rounded`}>
              {remoteUsers.length > 0 ? (
                <span className="text-white font-bold text-xs">LIVE</span>
              ) : agoraConnected ? (
                <>
                  <Wifi className="h-3 w-3 text-white" />
                  <span className="text-white font-bold text-xs">WAITING</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 text-white" />
                  <span className="text-white font-bold text-xs">CONNECTING</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 text-white">
              <User className="h-4 w-4" />
              <span className="text-sm font-medium">{viewerCount}</span>
            </div>
          </div>
        </div>

        {/* Join Notification */}
        {joinNotification && (
          <div className="relative z-10 px-3 animate-fade-in">
            <div className="flex items-center gap-2 text-white text-sm">
              <span className="text-xl">👋</span>
              <span><strong>{joinNotification}</strong> joined</span>
            </div>
          </div>
        )}

        {/* Explore */}
        <div className="relative z-10 flex justify-end px-3 mt-2">
          <button onClick={() => navigate('/live')} className="bg-slate-800/60 backdrop-blur px-3 py-1.5 rounded-full flex items-center gap-1">
            <span className="text-white text-sm">Explore</span>
            <ChevronRight className="h-4 w-4 text-white" />
          </button>
        </div>

        {/* Chat */}
        <div className="relative z-10 flex-1 flex flex-col justify-end px-3 pb-2 pointer-events-none">
          <div className="space-y-2 max-h-[200px] overflow-hidden">
            {chatMessages.map((msg) => (
              <div key={msg.id} className="flex items-start gap-2 animate-slide-up pointer-events-auto">
                <Avatar className="w-7 h-7 flex-shrink-0">
                  <AvatarFallback className={`text-xs ${msg.isMe ? 'bg-emerald-500' : 'bg-slate-600'} text-white`}>
                    {msg.avatar}
                  </AvatarFallback>
                </Avatar>
                <div className="bg-black/40 backdrop-blur-sm rounded-2xl px-3 py-1.5">
                  <span className="text-white text-sm">
                    <strong className="text-white/90">{msg.user}</strong>{' '}
                    <span className="text-white/80">{msg.message}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pointer-events-auto">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Ask or comment..."
              className="bg-white/90 border-0 rounded-full h-10 text-black"
            />
          </div>
        </div>

        {/* Right Actions */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-4">
          <button onClick={() => setLiked(!liked)} className={`w-10 h-10 rounded-full flex items-center justify-center ${liked ? 'bg-red-500' : 'bg-black/40'}`}>
            <Heart className={`h-6 w-6 ${liked ? 'text-white fill-white' : 'text-white'}`} />
          </button>
          <button onClick={handleShare} className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
            <Share2 className="h-5 w-5 text-white" />
          </button>
          <button onClick={() => setMuted(!muted)} className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
            {muted ? <VolumeX className="h-5 w-5 text-white" /> : <Volume2 className="h-5 w-5 text-white" />}
          </button>
        </div>

        {/* Winner */}
        {winner && (
          <div className="absolute left-3 bottom-[240px] z-20 animate-bounce-in">
            <div className="flex items-center gap-2 bg-emerald-500/90 rounded-full px-3 py-2">
              <Trophy className="w-5 h-5 text-yellow-300" />
              <Avatar className="w-6 h-6"><AvatarFallback className="bg-emerald-600 text-white text-xs">{winner[0]}</AvatarFallback></Avatar>
              <span className="text-white text-sm"><strong>{winner}</strong> won the item!</span>
            </div>
          </div>
        )}

        {/* Auction Countdown Timer */}
        {auction && auctionStatus === 'active' && auctionCountdown !== null && (
          <div className="absolute left-3 bottom-[280px] z-20">
            <div className={`flex items-center gap-2 rounded-full px-4 py-2 ${
              auctionCountdown <= 5 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'
            }`}>
              <Timer className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-lg">{auctionCountdown}s</span>
              {highBidder && (
                <span className="text-white/90 text-sm ml-2">
                  High: <strong>{highBidder}</strong> ${currentBid}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Product Card */}
        <div className="relative z-10 bg-white safe-area-bottom">
          <div className="flex items-center gap-3 p-3 border-b">
            <div className="relative w-16 h-16">
              <img src={auction?.listing_image || currentProduct?.image} alt="" className="w-full h-full object-cover rounded-lg" />
              {currentProduct?.sold && (
                <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                  <Badge className="bg-red-500 text-white text-xs">SOLD</Badge>
                </div>
              )}
              {auction && (
                <div className="absolute -top-1 -right-1">
                  <Badge className="bg-orange-500 text-white text-xs px-1.5">AUCTION</Badge>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm line-clamp-1">
                {auction?.listing_title || currentProduct?.title}
              </p>
              <p className="text-gray-500 text-xs">Size: {currentProduct?.size}</p>
              <p className="text-gray-500 text-xs">@{stream?.host_name || 'seller'}</p>
            </div>
            <div className="text-right">
              {auction ? (
                <div>
                  <p className="text-xs text-gray-500">Current Bid</p>
                  <p className="font-bold text-lg text-emerald-600">${currentBid || auction.starting_price}</p>
                </div>
              ) : (
                <p className="font-bold text-lg">${currentProduct?.price}</p>
              )}
            </div>
          </div>
          
          <div className="p-3">
            {auctionStatus === 'sold' || winner ? (
              <div className="bg-emerald-100 border border-emerald-300 rounded-lg py-3 text-center">
                <span className="text-emerald-700 font-medium flex items-center justify-center gap-2">
                  <Trophy className="w-5 h-5" />
                  {winner ? `${winner} won for $${currentBid}!` : 'Auction Has Ended'}
                </span>
              </div>
            ) : auction && auctionStatus === 'active' ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={bidInput}
                    onChange={(e) => setBidInput(e.target.value)}
                    placeholder={`Min: $${(currentBid || 0) + 1}`}
                    className="flex-1 h-12"
                    min={(currentBid || 0) + 1}
                  />
                  <Button 
                    onClick={handleBid} 
                    disabled={isPlacingBid}
                    className="bg-emerald-500 hover:bg-emerald-600 h-12 px-6"
                  >
                    <Gavel className="h-5 w-5 mr-2" /> 
                    {isPlacingBid ? 'Bidding...' : 'Bid'}
                  </Button>
                </div>
                <div className="flex justify-center gap-2">
                  {[5, 10, 25].map((inc) => (
                    <button
                      key={inc}
                      onClick={() => setBidInput(String((currentBid || auction.starting_price) + inc))}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium"
                    >
                      +${inc}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <Button onClick={handleBid} className="w-full bg-emerald-500 hover:bg-emerald-600 h-12">
                <Gavel className="h-5 w-5 mr-2" /> Place Bid - ${currentProduct?.price}
              </Button>
            )}
          </div>
        </div>

        <style>{`
          @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.8); } 50% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }
          .animate-fade-in { animation: fade-in 0.3s ease-out; }
          .animate-slide-up { animation: slide-up 0.3s ease-out; }
          .animate-bounce-in { animation: bounce-in 0.4s ease-out; }
          .safe-area-top { padding-top: env(safe-area-inset-top, 12px); }
          .safe-area-bottom { padding-bottom: env(safe-area-inset-bottom, 0); }
        `}</style>
      </div>
    );
  }

  // ============ DESKTOP VIEW - 3 COLUMN LAYOUT ============
  return (
    <div className="fixed inset-0 bg-black flex" data-testid="desktop-live-watch">
      {/* LEFT COLUMN - Products Sidebar */}
      <div className="w-80 bg-black border-r border-slate-800 flex flex-col">
        {/* Show Title */}
        <div className="p-4 border-b border-slate-800">
          <h1 className="text-white font-bold text-lg">{stream?.title || 'Live Show'}</h1>
        </div>
        
        {/* Products Header */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white text-sm font-medium">
              {products.length} LISTINGS IN SHOW
            </span>
            <button className="text-slate-400 hover:text-white">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search Listings"
              value={searchListings}
              onChange={(e) => setSearchListings(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700 text-white text-sm h-9"
            />
          </div>
        </div>
        
        {/* Products List */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            {filteredProducts.map((item) => (
              <div 
                key={item.id}
                onClick={() => setCurrentProduct(item)}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer mb-2 transition ${
                  currentProduct?.id === item.id 
                    ? 'bg-slate-800 border border-slate-600' 
                    : 'hover:bg-slate-900'
                }`}
              >
                <div className="relative w-16 h-20 flex-shrink-0">
                  <img src={item.image} alt="" className="w-full h-full object-cover rounded" />
                  {item.sold && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded">
                      <Badge className="bg-red-500 text-white text-xs">SOLD</Badge>
                    </div>
                  )}
                  {currentProduct?.id === item.id && !item.sold && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 bg-black/70 rounded-full flex items-center justify-center">
                        <div className="w-0 h-0 border-l-[8px] border-l-white border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent ml-1" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium line-clamp-1">{item.title}</p>
                  <p className="text-slate-400 text-xs">Size: {item.size} · {item.brand}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {item.nwt && <Badge className="bg-slate-700 text-white text-xs px-1 py-0">NWT</Badge>}
                    <span className="text-slate-400 text-xs">@{stream?.host_name || 'seller'}</span>
                    <span className="text-emerald-400 text-xs">●</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">${item.price}</p>
                  <div className="flex items-center gap-1 text-slate-400 mt-1">
                    <Heart className="h-3 w-3" />
                    <span className="text-xs">{item.likes}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* CENTER COLUMN - Video Player */}
      <div className="flex-1 flex flex-col relative">
        {/* Video Background - YouTube or Agora Container */}
        <div className="absolute inset-0">
          {stream?.youtube_video_id ? (
            <YouTubePlayer
              videoId={stream.youtube_video_id}
              title={stream?.title}
              thumbnail={stream?.thumbnail}
              autoplay={true}
              muted={muted}
              className="w-full h-full"
            />
          ) : (
            <>
              <div 
                ref={desktopVideoContainerRef}
                className="w-full h-full"
                style={{ background: '#000' }}
              />
              
              {/* Fallback when no video stream */}
              {remoteUsers.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                  {stream?.thumbnail ? (
                    <img src={stream.thumbnail} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                      {agoraConnected ? (
                        <div className="text-center">
                          <Radio className="h-32 w-32 text-slate-600 animate-pulse mx-auto mb-4" />
                          <p className="text-slate-400 text-lg">Waiting for host to start streaming...</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="animate-spin h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
                          <p className="text-slate-400 text-lg">Connecting to stream...</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Video Overlay Content */}
        <div className="relative z-10 flex-1 flex flex-col">
          {/* Top Bar */}
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Avatar className="w-10 h-10 border-2 border-white">
                  <AvatarImage src={stream?.host_picture} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                    {stream?.host_name?.[0] || 'H'}
                  </AvatarFallback>
                </Avatar>
                <button className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-black">
                  <span className="text-white text-xs font-bold">+</span>
                </button>
              </div>
              <span className="text-white font-medium">@{stream?.host_name || 'host'}</span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-white">
                <User className="h-4 w-4" />
                <span className="font-medium">{viewerCount}</span>
              </div>
              <Badge className={`${remoteUsers.length > 0 ? 'bg-red-500' : agoraConnected ? 'bg-yellow-500' : 'bg-slate-500'} text-white px-3 py-1`}>
                {remoteUsers.length > 0 ? '● LIVE' : agoraConnected ? '● WAITING' : '● CONNECTING'}
              </Badge>
            </div>
          </div>
          
          {/* Explore Button */}
          <div className="flex justify-end px-4">
            <button 
              onClick={() => navigate('/live')}
              className="bg-black/50 hover:bg-black/70 text-white px-4 py-2 rounded-full flex items-center gap-1 transition"
            >
              Explore <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          {/* Promo Banner */}
          {showPromo && (
            <div className="mx-4 mt-4">
              <div className="bg-white rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">📦</span>
                  <span><strong className="text-red-500">$1.99 SHIPPING</strong> on your first Live Shows purchase!</span>
                </div>
                <button onClick={() => setShowPromo(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
          
          {/* Spacer */}
          <div className="flex-1" />
          
          {/* Winner Announcement */}
          {winner && (
            <div className="mx-4 mb-3">
              <div className="flex items-center gap-2 bg-black/70 rounded-full px-4 py-2 w-fit">
                <Avatar className="w-7 h-7">
                  <AvatarFallback className="bg-yellow-500 text-white text-xs">{winner[0]}</AvatarFallback>
                </Avatar>
                <span className="text-white text-sm">
                  <strong>{winner}</strong> <span className="text-yellow-400">won the item!</span> 🤑
                </span>
                <button className="text-slate-400 ml-2">•••</button>
              </div>
            </div>
          )}
          
          {/* Current Product Card */}
          <div className="mx-4 mb-4">
            <div className="bg-black/80 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-3">
                {/* Nav Arrows */}
                <button className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10">
                  <ChevronUp className="h-4 w-4" />
                </button>
                
                {/* Product Image */}
                <div className="relative w-16 h-16 flex-shrink-0">
                  <img src={currentProduct?.image} alt="" className="w-full h-full object-cover rounded-lg" />
                  {currentProduct?.sold && (
                    <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center">
                      <Badge className="bg-red-500 text-white text-xs">SOLD</Badge>
                    </div>
                  )}
                </div>
                
                {/* Product Info */}
                <div className="flex-1 min-w-0 text-white">
                  <p className="font-medium text-sm line-clamp-1">
                    {auction?.listing_title || currentProduct?.title}
                  </p>
                  <p className="text-slate-400 text-xs">Size: {currentProduct?.size}</p>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 text-xs">@{stream?.host_name || 'seller'}</span>
                    <span className="text-emerald-400 text-xs">●</span>
                  </div>
                </div>
                
                {/* Price / Current Bid */}
                <div className="text-right">
                  {auction && auctionStatus === 'active' ? (
                    <div>
                      <p className="text-slate-400 text-xs">Current Bid</p>
                      <p className="text-emerald-400 font-bold text-lg">${currentBid || auction.starting_price}</p>
                      {highBidder && <p className="text-yellow-400 text-xs">{highBidder}</p>}
                    </div>
                  ) : (
                    <div className="text-white font-bold text-lg">${currentProduct?.price}</div>
                  )}
                </div>
                
                {/* Share */}
                <button onClick={handleShare} className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10">
                  <Share2 className="h-4 w-4" />
                </button>
                
                {/* Nav Down */}
                <button className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10">
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              
              {/* Auction Countdown */}
              {auction && auctionStatus === 'active' && auctionCountdown !== null && (
                <div className={`mt-3 flex items-center justify-center gap-3 py-2 rounded-lg ${
                  auctionCountdown <= 5 ? 'bg-red-500/20 animate-pulse' : 'bg-orange-500/20'
                }`}>
                  <Timer className={`w-5 h-5 ${auctionCountdown <= 5 ? 'text-red-400' : 'text-orange-400'}`} />
                  <span className={`font-bold text-xl ${auctionCountdown <= 5 ? 'text-red-400' : 'text-orange-400'}`}>
                    {auctionCountdown}s
                  </span>
                  {auctionCountdown <= 3 && (
                    <span className="text-white text-sm">
                      {auctionCountdown === 1 ? 'SOLD!' : auctionCountdown === 2 ? 'Going twice...' : 'Going once...'}
                    </span>
                  )}
                </div>
              )}
              
              {/* Auction Button */}
              <div className="mt-3">
                {auctionStatus === 'sold' || winner ? (
                  <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg py-3 text-center">
                    <span className="text-emerald-400 font-medium flex items-center justify-center gap-2">
                      <Trophy className="w-5 h-5" />
                      {winner ? `${winner} won for $${currentBid}!` : 'Auction Has Ended'}
                    </span>
                  </div>
                ) : auction && auctionStatus === 'active' ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={bidInput}
                        onChange={(e) => setBidInput(e.target.value)}
                        placeholder={`Min: $${(currentBid || 0) + 1}`}
                        className="flex-1 h-11 bg-slate-800 border-slate-600 text-white"
                        min={(currentBid || 0) + 1}
                      />
                      <Button 
                        onClick={handleBid} 
                        disabled={isPlacingBid}
                        className="bg-emerald-500 hover:bg-emerald-600 h-11 px-6"
                      >
                        <Gavel className="h-5 w-5 mr-2" /> 
                        {isPlacingBid ? '...' : 'Bid'}
                      </Button>
                    </div>
                    <div className="flex justify-center gap-2">
                      {[5, 10, 25].map((inc) => (
                        <button
                          key={inc}
                          onClick={() => setBidInput(String((currentBid || auction.starting_price) + inc))}
                          className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded-full text-sm font-medium text-white"
                        >
                          +${inc}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Button onClick={handleBid} className="w-full bg-emerald-500 hover:bg-emerald-600 h-11">
                    <Gavel className="h-5 w-5 mr-2" /> Place Bid - ${currentProduct?.price}
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Volume Control */}
          <div className="absolute bottom-4 right-4">
            <button onClick={() => setMuted(!muted)} className="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70">
              {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN - Comments/Activity */}
      <div className="w-80 bg-black border-l border-slate-800 flex flex-col">
        {/* Exit Show Button */}
        <div className="p-4 flex justify-end border-b border-slate-800">
          <Button 
            variant="outline" 
            onClick={() => navigate('/live')}
            className="border-slate-600 text-white hover:bg-slate-800"
          >
            Exit Show
          </Button>
        </div>
        
        {/* Comments Header */}
        <div className="p-4 border-b border-slate-800">
          <h2 className="text-white font-medium text-sm">COMMENTS / ACTIVITY</h2>
        </div>
        
        {/* Join Notification */}
        {joinNotification && (
          <div className="px-4 py-2 animate-fade-in">
            <span className="text-white text-sm">👋 <strong>{joinNotification}</strong> joined</span>
          </div>
        )}
        
        {/* Comments List */}
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-3 py-4">
            {chatMessages.map((msg) => (
              <div key={msg.id} className="flex items-start gap-2">
                <Avatar className="w-7 h-7 flex-shrink-0">
                  <AvatarFallback className={`text-xs ${msg.isMe ? 'bg-emerald-500' : 'bg-yellow-500'} text-white`}>
                    {msg.avatar}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-white text-sm">
                    <strong>{msg.user}</strong>{' '}
                    <span className="text-slate-300">{msg.message}</span>
                  </p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>
        
        {/* Chat Input */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Ask or comment..."
              className="flex-1 bg-slate-900 border-slate-700 text-white h-10"
            />
            <button 
              onClick={handleSendChat}
              className="w-10 h-10 bg-emerald-500 hover:bg-emerald-600 rounded-lg flex items-center justify-center text-white"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default LiveWatch;
